/**
 * FUNCIONES AUXILIARES
 * Utilidades para trabajar con el contenido del sistema declarativo
 */

import { experiences } from "../data/experiences.js";
import { sections } from "../data/sections.js";
import { pages } from "../data/pages.js";

/**
 * Obtiene una experiencia por su slug o ID
 * @param {string} identifier - Slug o ID de la experiencia
 * @returns {Object|null} - Experiencia encontrada o null
 */
export const getExperience = (identifier) => {
  return (
    Object.values(experiences).find(
      (exp) => exp.slug === identifier || exp.id === identifier
    ) || null
  );
};

/**
 * Obtiene una sección por su ID
 * @param {string} sectionId - ID de la sección
 * @returns {Object|null} - Sección encontrada o null
 */
export const getSection = (sectionId) => {
  const section = sections[sectionId];
  if (!section) return null;

  // Procesamiento especial para gamesShowcase
  if (sectionId === "gamesShowcase") {
    let games;

    // Si showAll es true, mostrar todas las experiencias
    if (section.data.showAll) {
      games = getAllExperiences();
    }
    // Si no, usar solo las featured
    else if (section.data.featured) {
      games = getFeaturedExperiences(section.data.featured);
    }

    return {
      ...section,
      data: {
        ...section.data,
        games: games || [],
      },
    };
  }

  return section;
};

/**
 * Obtiene una página por su ID
 * - Si existe en pages.js, regresa la página tal cual.
 * - Si NO existe pero el ID coincide con una experiencia (slug o id), genera
 *   una página por defecto basada en plantilla declarativa mínima.
 * @param {string} pageId - ID de la página (o slug de experiencia)
 * @returns {Object|null} - Página encontrada o generada, o null si no existe
 */
export const getPage = (pageId) => {
  // 1) Página explícita
  const existing = pages[pageId];
  if (existing) return existing;

  // 2) Fallback por experiencia (coincidencia por slug o id)
  const exp = getExperience(pageId);
  if (exp) {
    return {
      title: `${exp.title} - Zero Latency MTY`,
      description:
        exp.description || `Vive ${exp.title} en Zero Latency Monterrey`,
      keywords: Array.isArray(exp.tags) ? exp.tags : [],
      sections: [
        {
          type: "experience-details",
          id: `${exp.slug}-details`,
          data: { experienceID: exp.slug },
        },
        {
          type: "text-section",
          id: `${exp.slug}-about`,
          data: {
            title: exp.title,
            description: exp.description || "",
            textAlign: "center",
          },
          backgroundColor: "#00202C",
        },
      ],
    };
  }

  // 3) No existe
  return null;
};

/**
 * Obtiene todas las experiencias
 * @returns {Object[]} - Array de todas las experiencias
 */
export const getAllExperiences = () => {
  return Object.values(experiences);
};

/**
 * Obtiene experiencias por categoría
 * @param {string} category - Categoría a filtrar
 * @returns {Object[]} - Array de experiencias filtradas
 */
export const getExperiencesByCategory = (category) => {
  return Object.values(experiences).filter(
    (exp) => exp.category?.toLowerCase() === category?.toLowerCase()
  );
};

/**
 * Obtiene experiencias destacadas
 * @param {string[]} featuredIds - IDs de experiencias destacadas
 * @returns {Object[]} - Array de experiencias destacadas
 */
export const getFeaturedExperiences = (featuredIds = []) => {
  return featuredIds.map((id) => getExperience(id)).filter(Boolean);
};

/**
 * Resuelve las secciones de una página (convierte IDs en objetos)
 * @param {(string|Object)[]} pageSections - Array de secciones de la página
 * @returns {Object[]} - Array de objetos de sección resueltos
 */
export const resolvePageSections = (pageSections = []) => {
  /**
   * Filtra o resuelve secciones:
   * - Si es string, resuelve a un objeto definido en sections (o descarta si no existe).
   * - Si es objeto, solo acepta objetos con 'type' válido.
   * - Descarta silenciosamente entradas inválidas para evitar ruido visual.
   */
  return pageSections
    .map((section) => {
      if (typeof section === "string") {
        // Es un ID de sección, buscarla en sections
        const resolvedSection = getSection(section);
        if (!resolvedSection) {
          console.warn(`Sección '${section}' no encontrada`);
          return null;
        }
        return resolvedSection;
      }
      // Aceptar solo objetos con 'type' válido
      if (section && typeof section === "object" && section.type) {
        return section;
      }
      // Descartar objetos inválidos (sin type, null, etc.)
      return null;
    })
    .filter(Boolean);
};

/**
 * Genera metadatos SEO para una página
 * @param {Object} page - Objeto de página
 * @param {Object} [overrides] - Sobrescribir metadatos específicos
 * @returns {Object} - Metadatos SEO
 */
export const generateSEOMetadata = (page, overrides = {}) => {
  return {
    title: overrides.title || page.title,
    description: overrides.description || page.description,
    keywords: overrides.keywords || page.keywords?.join(", ") || "",
    ...overrides,
  };
};

/**
 * Obtiene la configuración responsive para un componente
 * @param {Object} data - Datos del componente
 * @param {string} device - Dispositivo (mobile, tablet, desktop)
 * @returns {Object} - Configuración responsive
 */
export const getResponsiveConfig = (data, device = "desktop") => {
  if (!data.responsive) return data;

  return {
    ...data,
    ...data.responsive[device],
  };
};

/**
 * Valida si una URL es externa
 * @param {string} url - URL a validar
 * @returns {boolean} - True si es externa
 */
export const isExternalUrl = (url) => {
  return url.startsWith("http://") || url.startsWith("https://");
};

/**
 * Formatea un slug para URL
 * @param {string} text - Texto a convertir en slug
 * @returns {string} - Slug formateado
 */
export const createSlug = (text) => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

/**
 * Obtiene experiencias populares (con badge "POPULAR")
 */
export const getPopularExperiences = () => {
  return Object.values(experiences).filter((exp) => exp.badge === "POPULAR");
};

/**
 * Obtiene experiencias nuevas (con badge "NUEVO")
 */
export const getNewExperiences = () => {
  return Object.values(experiences).filter((exp) => exp.badge === "NUEVO");
};

/**
 * Obtiene experiencias por dificultad
 */
export const getExperiencesByDifficulty = (difficulty) => {
  return Object.values(experiences).filter(
    (exp) => exp.difficulty?.toLowerCase() === difficulty?.toLowerCase()
  );
};
